import { DemoPaymentScenarioSchema } from './scenarios.js';
import { z } from 'zod';
import { BreBAliasTypeSchema, DocumentTypeSchema } from '../catalogs/domain-catalogs.js';
import { DepositAccountTypeSchema, ParticipantTypeSchema } from '../catalogs/banks.js';
import { BusinessDateSchema, TimestampSchema } from '../common/clock.js';
import { MoneySchema, PositiveMoneySchema } from '../common/money.js';
/**
 * API HTTP de `services/sandbox` (contratos para los adaptadores `sandbox` de `services/api` y para
 * el Portal Comercio Demo). Bre-B se modela como lo expone un **proveedor de servicios de
 * conectividad** contratado por la entidad: REST JSON con API key, `Idempotency-Key`, envío
 * asíncrono (202) y webhooks firmados (`Sbx-Signature`, mismo esquema HMAC `t=…,v1=…` que
 * `@paguz/server-kit`). Los códigos de rechazo son estilo ISO 20022 (`pacs.002`); el adaptador los
 * traduce a `PZ-BRB-*` con `BREB_REJECT_CODES`. Core de depósitos, centrales, KYC y firma siguen la
 * "opción A" del Kit de integración: REST 1:1 con el puerto (`POST /<sistema>/v1/<operación>`).
 */
// ── Cabeceras ─────────────────────────────────────────────────────────────
export const SANDBOX_HEADERS = {
    /** Regla aplicada (depuración y guion de demo). Presente en todas las respuestas. */
    scenario: 'X-Sandbox-Scenario',
    /** Firma HMAC de los webhooks salientes del sandbox. */
    signature: 'Sbx-Signature',
    eventId: 'Sbx-Event-Id',
    eventType: 'Sbx-Event-Type',
    idempotencyKey: 'Idempotency-Key',
};
/** Rutas relativas al `callback_url` base del participante (p. ej. `…/v1/integrations/breb`). */
export const BREB_WEBHOOK_PATHS = {
    transferStatus: '/transfer-status',
    incomingTransfer: '/incoming-transfer',
};
/** Reintentos de webhooks del sandbox (ms desde el intento anterior). Luego `FAILED`. */
export const SANDBOX_WEBHOOK_RETRY_SCHEDULE_MS = [
    1000, 2000, 5000, 10_000, 30_000, 60_000, 120_000, 300_000,
];
/** Callbacks de Bre-B: latencia configurable (`SANDBOX_BREB_LATENCY_MS`, 0,5–3 s). */
export const SANDBOX_BREB_LATENCY_BOUNDS_MS = { min: 500, max: 3000 };
/** `.99` / `.98`: la consulta de estado resuelve a los 60 s. */
export const SANDBOX_BREB_TIMEOUT_RESOLVE_MS = 60_000;
/** `.77`: callback tardío a los 25 s (después del SLA de 20 s). */
export const SANDBOX_BREB_LATE_CALLBACK_MS = 25_000;
/** `.55`: separación entre el callback y su duplicado. */
export const SANDBOX_BREB_DUPLICATE_GAP_MS = 300;
// ── Códigos de rechazo (estilo ISO 20022 ExternalStatusReason) ────────────
export const BREB_REJECT_CODES = {
    /** Rechazo del participante receptor (motivo no especificado). */
    MS03: { reasonCode: 'PZ-BRB-002', description: 'Rechazado por el participante receptor' },
    /** Llave (proxy) inexistente o inválida. */
    BE23: { reasonCode: 'PZ-BRB-001', description: 'Llave inexistente en el directorio' },
    /** Cuenta del beneficiario inválida o cerrada. */
    AC03: { reasonCode: 'PZ-BRB-006', description: 'Cuenta destino inválida' },
    /** Cuenta del beneficiario bloqueada. */
    AC06: { reasonCode: 'PZ-BRB-006', description: 'Cuenta destino bloqueada' },
    /** Monto no permitido por el esquema. */
    AM02: { reasonCode: 'PZ-BRB-008', description: 'Monto por encima del máximo del esquema' },
    /** El participante receptor no respondió. */
    AB06: { reasonCode: 'PZ-BRB-005', description: 'Participante receptor no disponible' },
};
export const BrebRejectCodeSchema = z.enum(Object.keys(BREB_REJECT_CODES));
/** Traduce un código externo a `PZ-BRB-*` (desconocido → `PZ-BRB-002`). */
export function brebRejectReason(code) {
    const entry = BREB_REJECT_CODES[code ?? ''];
    return entry?.reasonCode ?? 'PZ-BRB-002';
}
// ── Directorio de llaves ──────────────────────────────────────────────────
export const SbxParticipantSchema = z
    .object({ code: z.string(), name: z.string(), type: ParticipantTypeSchema })
    .meta({ id: 'SbxParticipant' });
export const SbxResolveAliasRequestSchema = z
    .object({ aliasType: BreBAliasTypeSchema, aliasValue: z.string().min(1).max(100) })
    .meta({ id: 'SbxResolveAliasRequest' });
export const SbxResolveAliasResponseSchema = z
    .object({
    aliasType: BreBAliasTypeSchema,
    /** Llave normalizada (celular sin espacios, `@alias` en minúscula…). */
    aliasValue: z.string(),
    participant: SbxParticipantSchema,
    /** Personas: nombre parcialmente enmascarado; comercios: razón social. */
    holderName: z.string(),
    holderType: z.enum(['PERSON', 'MERCHANT']),
    account: z.object({ maskedNumber: z.string(), type: DepositAccountTypeSchema }),
    merchant: z
        .object({ name: z.string(), mcc: z.string(), city: z.string().nullable() })
        .nullable(),
    /** `true` si el titular se generó de forma determinística (llave no sembrada). */
    synthetic: z.boolean(),
})
    .meta({ id: 'SbxResolveAliasResponse' });
export const SbxMerchantSchema = z
    .object({
    aliasType: BreBAliasTypeSchema,
    aliasValue: z.string(),
    merchantName: z.string(),
    holderName: z.string(),
    mcc: z.string(),
    city: z.string().nullable(),
    participant: SbxParticipantSchema,
})
    .meta({ id: 'SbxMerchant' });
// ── Transferencias ────────────────────────────────────────────────────────
export const SbxTransferStatusSchema = z.enum(['RECEIVED', 'PROCESSING', 'ACCEPTED', 'REJECTED']);
/** `POST /breb/v1/transfers` (cabecera `Idempotency-Key` obligatoria) — equivale a `pacs.008`. */
export const SbxSendTransferRequestSchema = z
    .object({
    endToEndId: z.string().min(8).max(64),
    debtor: z.object({
        participantCode: z.string().regex(/^\d{1,6}$/),
        accountNumber: z.string().min(4).max(34),
        accountType: z.string().min(1).max(30),
        holderName: z.string().min(1).max(140),
        holderDocument: z.string().max(20).optional(),
    }),
    creditor: z.object({ aliasType: BreBAliasTypeSchema, aliasValue: z.string().min(1).max(100) }),
    amount: PositiveMoneySchema,
    /** Referencia del QR dinámico o `pi_…`. */
    reference: z.string().max(64).optional(),
    remittanceInfo: z.string().max(140).optional(),
    /** Solo sandbox: escenario elegido en el demo; tiene prioridad sobre reglas y centavos. */
    scenario: DemoPaymentScenarioSchema.optional(),
})
    .meta({ id: 'SbxSendTransferRequest' });
export const SbxTransferSchema = z
    .object({
    endToEndId: z.string(),
    direction: z.enum(['OUTGOING', 'INCOMING']),
    status: SbxTransferStatusSchema,
    amount: MoneySchema,
    debtor: z.object({
        participantCode: z.string(),
        name: z.string(),
        maskedAccount: z.string(),
    }),
    creditor: z.object({
        aliasType: BreBAliasTypeSchema.nullable(),
        aliasValue: z.string().nullable(),
        participantCode: z.string(),
        name: z.string(),
        maskedAccount: z.string(),
    }),
    reference: z.string().nullable(),
    remittanceInfo: z.string().nullable(),
    originalEndToEndId: z.string().nullable(),
    rejectCode: z.string().nullable(),
    rejectReason: z.string().nullable(),
    createdAt: TimestampSchema,
    settledAt: TimestampSchema.nullable(),
    /** Regla de escenario aplicada (`NORMAL`, `CENTS_13_REJECT`, `FAULT_RULE:<id>`…). */
    scenario: z.string(),
})
    .meta({ id: 'SbxTransfer' });
/** `POST /breb/v1/sandbox/incoming` — devolución del comercio (transferencia entrante simulada). */
export const SbxIncomingTransferRequestSchema = z
    .object({
    /** Transferencia original (compra) que se devuelve total o parcialmente. */
    originalEndToEndId: z.string().min(8).max(64),
    amount: PositiveMoneySchema,
    remittanceInfo: z.string().max(140).optional(),
})
    .meta({ id: 'SbxIncomingTransferRequest' });
export const SbxSettlementReportSchema = z
    .object({
    businessDate: BusinessDateSchema,
    participantCode: z.string(),
    entries: z.array(z.object({
        endToEndId: z.string(),
        direction: z.enum(['OUTGOING', 'INCOMING']),
        amount: MoneySchema,
        status: z.enum(['ACCEPTED', 'REJECTED']),
        counterpartyParticipant: z.string(),
        settledAt: TimestampSchema,
    })),
    totals: z.object({ outgoing: MoneySchema, incoming: MoneySchema, count: z.int().min(0) }),
    generatedAt: TimestampSchema,
})
    .meta({ id: 'SbxSettlementReport' });
// ── Webhooks salientes del sandbox ────────────────────────────────────────
/** `POST {callback_url}/transfer-status` — equivale a `pacs.002`. Idempotente por (e2e, estado). */
export const SbxTransferStatusWebhookSchema = z
    .object({
    eventId: z.string(),
    eventType: z.literal('transfer.status'),
    occurredAt: TimestampSchema,
    data: z.object({
        endToEndId: z.string(),
        status: z.enum(['ACCEPTED', 'REJECTED']),
        rejectCode: z.string().nullable(),
        rejectReason: z.string().nullable(),
        amount: MoneySchema,
        reference: z.string().nullable(),
        settledAt: TimestampSchema,
    }),
})
    .meta({ id: 'SbxTransferStatusWebhook' });
/** `POST {callback_url}/incoming-transfer` — devolución (`pacs.004`/`pacs.008`). Idempotente por e2e. */
export const SbxIncomingTransferWebhookSchema = z
    .object({
    eventId: z.string(),
    eventType: z.literal('transfer.incoming'),
    occurredAt: TimestampSchema,
    data: z.object({
        endToEndId: z.string(),
        originalEndToEndId: z.string().nullable(),
        reference: z.string().nullable(),
        payer: z.object({
            aliasType: BreBAliasTypeSchema.nullable(),
            aliasValue: z.string().nullable(),
            participantCode: z.string(),
            name: z.string(),
        }),
        creditor: z.object({ participantCode: z.string(), accountNumber: z.string() }),
        amount: MoneySchema,
        remittanceInfo: z.string().nullable(),
        settledAt: TimestampSchema,
    }),
})
    .meta({ id: 'SbxIncomingTransferWebhook' });
/** `POST {callbackUrl}` del sobre de firma (2 s después de firmar). */
export const SbxESignCallbackSchema = z
    .object({
    eventId: z.string(),
    eventType: z.literal('envelope.status'),
    occurredAt: TimestampSchema,
    data: z.object({
        envelopeId: z.string(),
        externalRef: z.string(),
        status: z.enum(['SENT', 'SIGNED', 'DECLINED', 'EXPIRED']),
        expiresAt: TimestampSchema,
        signedAt: TimestampSchema.nullable(),
        signedDocumentBlobPath: z.string().nullable(),
    }),
})
    .meta({ id: 'SbxESignCallback' });
// ── Eventos SSE por llave (Portal Comercio) ───────────────────────────────
/**
 * `GET /breb/v1/sandbox/aliases/events?aliasType=…&aliasValue=…` (`text/event-stream`,
 * `retry: 2000`). `event: transfer` cada vez que una transferencia hacia/desde la llave cambia de estado.
 */
export const SbxAliasEventSchema = z
    .object({
    type: z.enum(['PAYMENT_RECEIVED', 'PAYMENT_REJECTED', 'REFUND_SENT']),
    transfer: SbxTransferSchema,
    /** Total devuelto hasta ahora sobre la transferencia original (solo pagos recibidos). */
    refundedAmount: MoneySchema,
})
    .meta({ id: 'SbxAliasEvent' });
export const SbxAliasActivitySchema = z
    .object({ data: z.array(SbxAliasEventSchema) })
    .meta({ id: 'SbxAliasActivity' });
/** `POST /breb/v1/sandbox/qr` — QR de un comercio sembrado (estático o dinámico). */
export const SbxGenerateQrRequestSchema = z
    .object({
    aliasType: BreBAliasTypeSchema,
    aliasValue: z.string().min(1).max(100),
    amount: PositiveMoneySchema.optional(),
    reference: z.string().max(25).optional(),
    /** Vigencia del QR dinámico (por defecto 15 min). */
    ttlSeconds: z.int().min(60).max(86_400).optional(),
})
    .meta({ id: 'SbxGenerateQrRequest' });
export const SbxGenerateQrResponseSchema = z
    .object({
    payload: z.string(),
    pngDataUrl: z.string(),
    qrType: z.enum(['STATIC', 'DYNAMIC']),
    expiresAt: TimestampSchema.nullable(),
})
    .meta({ id: 'SbxGenerateQrResponse' });
// ── Inyección de fallas (Controles de demo) ───────────────────────────────
export const SbxFaultModeStateSchema = z
    .object({
    mode: z.enum(['NORMAL', 'REJECT', 'TIMEOUT', 'LATE_CALLBACK', 'DUPLICATE_CALLBACK']),
    remaining: z.int().min(0),
    latencyMs: z.int().min(0).nullable(),
})
    .meta({ id: 'SbxFaultModeState' });
// ── Opción A: puertos 1:1 por HTTP ────────────────────────────────────────
const PersonRefSchema = z.object({
    documentType: DocumentTypeSchema,
    documentNumber: z.string().min(3).max(20),
    fullName: z.string().min(1).max(140),
});
export const SbxPersonRefSchema = PersonRefSchema.meta({ id: 'SbxPersonRef' });
/** Operaciones HTTP de los puertos simulados (`POST /<prefijo>/<operación>`, cuerpo = entrada del puerto). */
export const SANDBOX_PORT_ROUTES = {
    depositCore: {
        prefix: '/deposit-core/v1',
        operations: [
            'getAccount',
            'validateAccount',
            'creditAndHold',
            'debitHeld',
            'reverseCredit',
            'postMovement',
            'listMovements',
        ],
    },
    bureau: { prefix: '/bureau/v1', operations: ['getReport'] },
    kyc: { prefix: '/kyc/v1', operations: ['verifyIdentity', 'screenSanctions'] },
    esign: { prefix: '/esign/v1', operations: ['createEnvelope', 'getStatus', 'confirmSignature'] },
};
//# sourceMappingURL=api.js.map